export const en = {
  app: {
    tagline: 'Smarter Markets. Greater Opportunities.',
  },
  nav: {
    home: 'Home',
    markets: 'Markets',
    convert: 'Convert',
    forecasts: 'Forecasts',
    more: 'More',
    pro: 'GlobalX Pro',
    settings: 'Settings',
    search: 'Search currencies…',
    menu: 'Main navigation',
    skip: 'Skip to content',
  },
  common: {
    demo: 'Demo',
    demoData: 'Demo data — not real prices',
    liveRatesNotice: 'Prices are real, with their source and time. Some markets are not connected yet; forecasts are a demo layout.',
    ad: 'Advertisement',
    adSlot: 'Ad space (free plan)',
    viewAll: 'View all',
    close: 'Close',
    comingSoon: 'Coming soon',
    justNow: 'just now',
    source: 'Source',
    updated: 'Updated',
    type: 'Price type',
    precision: 'Precision',
    notFound: 'Page not found',
    backHome: 'Back to home',
    proBadge: 'PRO',
  },
  home: {
    heroTitle: 'Smarter markets. Greater opportunities.',
    heroSubtitle: 'Convert currencies instantly and follow gold, silver, oil and crypto in one place.',
    quick: 'Quick conversions',
    snapshot: 'Market snapshot',
    movers: 'Top movers',
    openConverter: 'Full converter',
    proTitle: 'GlobalX Pro',
    proText: 'No ads, advanced alerts and deeper analytics.',
    proCta: 'Explore Pro',
    point1: 'Every price shows its source and update time',
    point2: 'Precision that follows each currency’s decimals',
    point3: 'Arabic and English, light and dark',
  },
  converter: {
    title: 'Currency converter',
    from: 'From',
    to: 'To',
    amount: 'Amount',
    swap: 'Swap currencies',
    rate: 'Exchange rate',
    copy: 'Copy result',
    copied: 'Copied',
    popular: 'Common amounts',
    pinPair: 'Save pair',
    unpinPair: 'Remove saved pair',
    savedPairs: 'Saved pairs',
    multiTitle: 'In other currencies',
    multiHint: 'Your favorite currencies at the current amount',
    invalid: 'Enter a valid amount',
    select: 'Choose currency',
    search: 'Search by code or name',
    noResults: 'No currency matches “{{q}}”',
    favorites: 'Favorites',
    popularList: 'Popular',
    all: 'All currencies',
    addFavorite: 'Add to favorites',
    removeFavorite: 'Remove from favorites',
    stale: 'These rates may be outdated. Last update: {{time}}',
    offline: 'You are offline. Showing the last saved rates.',
    cached: 'Could not reach the rates service. Showing the last rates saved on this device.',
    loadError: 'Could not load exchange rates. Check your connection and try again.',
    retry: 'Retry',
    dailyRate: 'daily rate',
    yerNotice:
      'Yemeni rial: this is the official reference rate. Market rates in Aden and Sanaa differ significantly from it.',
    deviation: 'Sources differ by {{pct}}% for {{code}}; treat this rate with caution.',
    unavailable: 'Rate unavailable for {{code}}',
    kind: {
      reference: 'Reference rate',
      delayed: 'Delayed price',
      live: 'Live price',
      demo: 'Demo data',
    },
    kindHint: {
      reference: 'Mid-market reference rate. Banks and exchanges apply their own spreads and fees.',
      delayed: 'Market price delayed by the provider; not an executable quote.',
      live: 'Streaming price from a licensed source.',
      demo: 'Illustrative values for interface design only. Do not use them for decisions.',
    },
    precision: '{{n}} decimal places for {{code}}',
  },
  markets: {
    title: 'Markets',
    subtitle: 'Currencies, crypto, precious metals and energy — each in its own section.',
    tabs: { all: 'All', watchlist: 'Watchlist', fiat: 'Currencies', crypto: 'Crypto', gold: 'Gold', silver: 'Silver', oil: 'Oil' },
    asset: 'Asset',
    price: 'Price',
    change: '24h',
    trend: '7-day trend',
    dataNote:
      'Prices are reference or delayed quotes from the named sources, not executable trading prices. Oil prices are daily closes that the source publishes several days later.',
    unavailable: 'Unavailable',
    unavailableText: 'This price is temporarily unavailable from its source. Please try again later.',
    comingSoonText: 'This market will be connected to a licensed free source in an upcoming step. No placeholder prices are shown.',
    loadError: 'Could not load market prices. Check your connection and try again.',
    historyError: 'Could not load the chart for this period.',
    since: 'vs. close of {{date}}',
    last24h: 'over the last 24 hours',
    sources: 'Sources:',
    changeBasis: 'Change basis',
    unit: 'Unit',
    about: 'About this price',
    notFound: 'Asset not found',
    openInConverter: 'Open in converter',
    priceFrom: 'Price: {{source}}, {{time}}',
    chartFrom: 'Chart: {{source}}',
    moversNote: 'Change vs. the previous close (crypto: last 24 hours).',
    notAdvice: 'Information only — not investment advice.',
  },
  assets: {
    XAU: 'Gold',
    XAG: 'Silver',
    BRENT: 'Brent Oil',
    WTI: 'WTI Oil',
    BTC: 'Bitcoin',
    ETH: 'Ethereum',
    SOL: 'Solana',
    EURUSD: 'EUR/USD',
    GBPUSD: 'GBP/USD',
    USDJPY: 'USD/JPY',
    USDCHF: 'USD/CHF',
    USDTRY: 'USD/TRY',
    USDEGP: 'USD/EGP',
    perOunce: 'per troy ounce',
    perBarrel: 'per barrel',
  },
  chart: {
    periods: { '1D': '1D', '1W': '1W', '1M': '1M', '3M': '3M', '1Y': '1Y', ALL: 'All' },
    high: 'High',
    low: 'Low',
    open: 'Open',
    period: 'Chart period',
    periodChange: 'Period change',
  },
  forecasts: {
    title: 'Forecasts',
    subtitle: 'Probabilistic outlooks — separate from descriptive analysis.',
    notReady: 'No real forecasts yet',
    notReadyText:
      'Forecasts will only be published once there is enough data and a method that has been back-tested historically. The layout below uses demo data.',
    horizon: 'Horizon',
    horizons: { '1M': '1 month', '3M': '3 months', '6M': '6 months', '12M': '12 months' },
    scenarios: 'Scenarios',
    bull: 'Upside',
    base: 'Central',
    bear: 'Downside',
    range: 'Range',
    probability: 'Probability',
    band: 'Illustrative interval',
    history: 'History',
    notComputed: 'not computed',
    demoNote: 'Layout preview with demo values — not a forecast.',
    methodology: 'Methodology',
    methodologyText:
      'Planned: probability ranges estimated from historical volatility and evaluated by back-testing (coverage and calibration of intervals) before publication.',
    uncertainty: 'Uncertainty',
    uncertaintyText:
      'Ranges widen with the horizon. Markets can move outside any interval due to events no model captures.',
    disclaimer: 'Forecasts are probabilistic, not guarantees, and are not investment advice.',
  },
  pro: {
    title: 'GlobalX Pro',
    subtitle: 'More tools for people who follow markets closely.',
    free: 'Free',
    pro: 'Pro',
    monthly: 'Monthly',
    yearly: 'Yearly',
    perMonth: '/ month',
    perYear: '/ year',
    hypothesis: 'Prices are initial hypotheses under review — subscriptions are not open yet.',
    cta: 'Subscriptions coming soon',
    features: {
      converter: 'Instant converter with favorites',
      markets: 'Market sections and charts',
      ads: 'No ads',
      alerts: 'Advanced price alerts',
      watchlists: 'Larger watchlists',
      charts: 'Deeper charts and analytics',
      compare: 'Asset comparisons',
      reports: 'Custom reports',
    },
  },
  settings: {
    title: 'Settings',
    appearance: 'Appearance',
    language: 'Language',
    theme: 'Theme',
    themes: { system: 'System', dark: 'Dark', light: 'Light' },
    digits: 'Numerals',
    digitStyles: { latn: '123', arab: '١٢٣' },
    data: 'Data & privacy',
    dataText: 'Your favorites, watchlist and preferences are stored on this device only. No account is required.',
    reset: 'Reset local data',
    resetDone: 'Local data cleared',
    about: 'About the data',
    aboutLink: 'About & privacy',
    aboutText:
      'Prices shown during this stage are demo data. Each price will display its source, update time and type once providers are connected.',
  },
  watch: {
    add: 'Add to watchlist',
    remove: 'Remove from watchlist',
    full: 'Watchlist is full ({{n}} assets on the free plan)',
    emptyTitle: 'Your watchlist is empty',
    emptyText: 'Tap the star on any asset to follow it here. Up to {{n}} assets, saved on this device.',
    missing: 'no longer available',
  },
  more: { title: 'More' },
  about: {
    title: 'About & privacy',
    subtitle: 'What GlobalX stores, where prices come from, and how to delete your data.',
    deviceTitle: 'Stored on your device only',
    deviceText:
      'Your language, theme, numerals, favorite currencies, saved pairs, watchlist, last conversion and the last prices received are saved in this browser (local storage) so the app works quickly and offline.',
    noAccount: 'No account is needed. GlobalX does not set tracking cookies and has no ads or analytics at this stage.',
    serversTitle: 'Our servers',
    serversText:
      'To show prices, the app requests them from GlobalX servers (Supabase, Frankfurt region), which fetch them from the sources below and keep only a cache of prices — no personal data.',
    hostsText:
      'Like any website, our hosting providers (Cloudflare and Supabase) may process technical data such as IP addresses to deliver and protect the service, under their own policies.',
    deleteTitle: 'Delete your data',
    deleteText: 'Use “Reset local data” in Settings, or clear this site’s data in your browser.',
    sourcesTitle: 'Data sources',
    sourcesText: 'Every price in the app shows its source, update time and type. Sources currently used or prepared:',
    source: {
      frankfurter: 'daily reference exchange rates and gold/silver history',
      exchangerate: 'reference exchange rates (when configured)',
      goldapi: 'gold and silver spot prices',
      eia: 'Brent and WTI daily closing prices',
      cmc: 'cryptocurrency prices (when configured)',
      lcw: 'cryptocurrency prices and history (when configured)',
      charts: 'chart rendering',
    },
    disclaimerTitle: 'Important',
    disclaimerText:
      'Prices are reference or delayed values for information only — not executable trading prices — and nothing in GlobalX is investment advice. Banks and exchanges apply their own rates and fees.',
    updated: 'Last updated: {{date}}',
  },
} as const

type Widen<T> = { [K in keyof T]: T[K] extends string ? string : Widen<T[K]> }
export type Translation = Widen<typeof en>
